import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCategories, deleteCategory, updateCategory } from '../../shared/api/categories';
import { Category } from '../../shared/types/header';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminCategoriesPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import type { Modifier } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './CategoryList.css';

interface SortableCategoryRowProps {
  category: Category;
  onDelete: (category: Category) => void;
  deletingCategoryId: string | null;
  adminCategoriesPath: string;
  isEditMode: boolean;
}

function SortableCategoryRow({ category, onDelete, deletingCategoryId, adminCategoriesPath, isEditMode }: SortableCategoryRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="category-list__row">
      <td className="category-list__td">
        <span className="category-list__id">{category.id}</span>
      </td>
      <td className="category-list__td">
        <Link
          to={`${adminCategoriesPath}/${category.id}/edit`}
          className="category-list__link"
        >
          {category.name}
        </Link>
      </td>
      <td className="category-list__td">
        {category.nameEn || '-'}
      </td>
      <td className="category-list__td">
        <code className="category-list__slug">{category.slug}</code>
      </td>
      <td className="category-list__td">
        <span className={`category-list__visibility-badge ${
          category.isVisible 
            ? 'category-list__visibility-badge--visible' 
            : 'category-list__visibility-badge--hidden'
        }`}>
          {category.isVisible ? '表示' : '非表示'}
        </span>
      </td>
      <td className="category-list__td">
        <span className="category-list__order-display">{category.order ?? '-'}</span>
      </td>
      <td className="category-list__td">
        <div className="category-list__actions-cell">
          <button
            className="category-list__button category-list__button--delete"
            onClick={() => onDelete(category)}
            disabled={deletingCategoryId === category.id || isEditMode}
          >
            {deletingCategoryId === category.id ? '削除中...' : '削除'}
          </button>
        </div>
      </td>
      {isEditMode && (
        <td className="category-list__td category-list__td--drag-handle">
          <button
            {...attributes}
            {...listeners}
            className="category-list__drag-handle"
            aria-label="ドラッグして順番を変更"
            title="ドラッグして順番を変更"
          >
            ⋮⋮
          </button>
        </td>
      )}
    </tr>
  );
}

function CategoryList() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminCategoriesPath = getAdminCategoriesPath();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCategories();
      // orderで昇順ソート、orderが同じ場合はcreated_atで昇順ソート
      const sortedCategories = [...response.categories].sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // orderが同じ場合はidで比較（created_atがない場合のフォールバック）
        return a.id.localeCompare(b.id);
      });
      setCategories(sortedCategories);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('カテゴリーの取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDelete = async (category: Category) => {
    const confirmed = window.confirm(
      `「${category.name}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingCategoryId(category.id);
      await deleteCategory(category.id);
      await fetchCategories();
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategories((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveOrder = async () => {
    if (!isEditMode) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // 新しい順番を計算（1から始まる連番）
      const updatePromises = categories.map((category, index) => {
        const newOrder = index + 1;
        // 順番が変更されていない場合はスキップ
        if (category.order === newOrder) {
          return Promise.resolve();
        }

        // 既存のカテゴリーデータを含めて送信（APIがnameを必須としているため）
        const updateData: {
          name?: string;
          name_en?: string;
          slug?: string;
          is_visible?: boolean;
          order?: number;
        } = {
          name: category.name,
          ...(category.nameEn && { name_en: category.nameEn }),
          slug: category.slug,
          is_visible: category.isVisible,
          order: newOrder,
        };

        return updateCategory(category.id, updateData);
      });

      await Promise.all(updatePromises);
      await fetchCategories();
      setIsEditMode(false);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(`順番の保存に失敗しました: ${err.message}`);
        alert(`順番の保存に失敗しました: ${err.message}`);
      } else {
        setError('順番の保存に失敗しました');
        alert('順番の保存に失敗しました');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    // 元の順番に戻すために再取得
    fetchCategories();
  };

  const categoryIds = useMemo(() => categories.map((cat) => cat.id), [categories]);

  // カスタムモディファイアー: 垂直方向のみに制限し、親要素内に制限
  const restrictToTableRows: Modifier = (args) => {
    // まず垂直方向のみに制限
    const verticalTransform = restrictToVerticalAxis(args);
    
    // 次に親要素内に制限
    return restrictToParentElement({
      ...args,
      transform: verticalTransform,
    });
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-layout__main">
        <div className="category-list">
          <header className="category-list__header">
            <h1 className="category-list__title">カテゴリー管理</h1>
            <div className="category-list__actions">
              {isEditMode ? (
                <>
                  <button
                    className="category-list__button category-list__button--secondary"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    キャンセル
                  </button>
                  <button
                    className="category-list__button category-list__button--primary"
                    onClick={handleSaveOrder}
                    disabled={isSaving}
                  >
                    {isSaving ? '保存中...' : '確定'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="category-list__button category-list__button--secondary"
                    onClick={() => setIsEditMode(true)}
                  >
                    順番を編集
                  </button>
                  <Link to={`${adminCategoriesPath}/new`} className="category-list__button category-list__button--primary">
                    新規作成
                  </Link>
                </>
              )}
            </div>
          </header>
          <main className="category-list__content">
            {loading ? (
              <p className="category-list__loading">読み込み中...</p>
            ) : error ? (
              <p className="category-list__error">{error}</p>
            ) : categories.length === 0 ? (
              <p className="category-list__empty">カテゴリーがありません</p>
            ) : (
              <div className="category-list__table-container" id="category-table-container">
                {isEditMode ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToTableRows]}
                  >
                    <table className="category-list__table">
                      <thead>
                        <tr>
                          <th className="category-list__th">ID</th>
                          <th className="category-list__th">名前（日本語）</th>
                          <th className="category-list__th">名前（英語）</th>
                          <th className="category-list__th">スラッグ</th>
                          <th className="category-list__th">表示</th>
                          <th className="category-list__th">順番</th>
                          <th className="category-list__th">操作</th>
                          <th className="category-list__th">ドラッグ</th>
                        </tr>
                      </thead>
                      <tbody>
                        <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                          {categories.map((category) => (
                            <SortableCategoryRow
                              key={category.id}
                              category={category}
                              onDelete={handleDelete}
                              deletingCategoryId={deletingCategoryId}
                              adminCategoriesPath={adminCategoriesPath}
                              isEditMode={isEditMode}
                            />
                          ))}
                        </SortableContext>
                      </tbody>
                    </table>
                  </DndContext>
                ) : (
                  <table className="category-list__table">
                    <thead>
                      <tr>
                        <th className="category-list__th">ID</th>
                        <th className="category-list__th">名前（日本語）</th>
                        <th className="category-list__th">名前（英語）</th>
                        <th className="category-list__th">スラッグ</th>
                        <th className="category-list__th">表示</th>
                        <th className="category-list__th">順番</th>
                        <th className="category-list__th">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category) => (
                        <tr key={category.id} className="category-list__row">
                          <td className="category-list__td">
                            <span className="category-list__id">{category.id}</span>
                          </td>
                          <td className="category-list__td">
                            <Link
                              to={`${adminCategoriesPath}/${category.id}/edit`}
                              className="category-list__link"
                            >
                              {category.name}
                            </Link>
                          </td>
                          <td className="category-list__td">
                            {category.nameEn || '-'}
                          </td>
                          <td className="category-list__td">
                            <code className="category-list__slug">{category.slug}</code>
                          </td>
                          <td className="category-list__td">
                            <span className={`category-list__visibility-badge ${
                              category.isVisible 
                                ? 'category-list__visibility-badge--visible' 
                                : 'category-list__visibility-badge--hidden'
                            }`}>
                              {category.isVisible ? '表示' : '非表示'}
                            </span>
                          </td>
                          <td className="category-list__td">
                            <span className="category-list__order-display">{category.order ?? '-'}</span>
                          </td>
                          <td className="category-list__td">
                            <div className="category-list__actions-cell">
                              <button
                                className="category-list__button category-list__button--delete"
                                onClick={() => handleDelete(category)}
                                disabled={deletingCategoryId === category.id}
                              >
                                {deletingCategoryId === category.id ? '削除中...' : '削除'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default CategoryList;
